import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { WhatsAppGlyph, ArrowL } from '../components/Icons.jsx';
import { LeadFormBand } from '../components/LeadForm.jsx';
import { FitSection, FAQSection } from '../components/ServiceHelpers.jsx';
import SEO from '../components/SEO.jsx';
import useReveal from '../hooks/useReveal.js';
import { WA_URL } from '../config.js';
import { buildServiceSchema, localBusinessSchema } from '../data/schemas.js';

// ─── PROJECT MANAGEMENT ───────────────────────────────────────
function PMServicePage() {
  useReveal();
  const [mode, setMode] = useState('turnkey');

  const turnkey = {
    name: 'מודל מלא',
    sub: 'פוקוס מוד לוקחת את כל הפרויקט.\nא׳ עד ת׳.',
    bullets: [
      'ניהול גם של ספקים מאחורי הקלעים',
      'מנהל פרויקט אחד מול הלקוח',
      'הלקוח מקבל סטטוס. לא מתעסק בתפעול',
      'הסכמי SLA וביצוע מול ספקי משנה',
      'אחריות מקצה לקצה',
    ],
    fit: 'ארגון שרוצה תוצאה.\nלא רוצה לנהל את הספקים בעצמו.',
  };

  const transparent = {
    name: 'מודל שקוף',
    sub: 'פוקוס מוד מנהלת מאחורי הקלעים.\nהלקוח מנהל מול הספקים בעצמו.',
    bullets: [
      'ישיבות שבועיות עם הצוות הפנימי',
      'תכנון, בקרת איכות, סדר עדיפויות',
      'הלקוח רואה ושומע הכל בזמן אמת',
      'ידע נשאר בארגון, לא יוצא איתנו',
      'מתאים לארגונים שרוצים ללמוד תוך כדי',
    ],
    fit: 'ארגון שרוצה לבנות יכולת פנימית.\nולא להישען לאורך זמן על ספק חיצוני.',
  };

  const active = mode === 'turnkey' ? turnkey : transparent;

  return (
    <main className="svc-page" data-accent="yellow">
      <SEO title="ניהול פרויקטים" description="פוקוס מוד נכנסת לפרויקטים מורכבים. מפרקת את המציאות. בונה תשתית עבודה נכונה. שני מודלי עבודה." path="/services/project-management" schema={[localBusinessSchema, buildServiceSchema({ title: 'ניהול פרויקטים', slug: 'project-management', description: 'פוקוס מוד נכנסת לפרויקטים מורכבים. מפרקת את המציאות. בונה תשתית עבודה נכונה. שני מודלי עבודה.' })]} />
      <section className="svc-head">
        <div className="wrap">
          <Link className="svc-back" to="/"><ArrowL size={14} /> חזרה לדף הבית</Link>
          <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>שירות · ניהול פרויקטים</div>
          <h1 className="svc-title">
            פרויקט מורכב צריך<br/>
            <span className="hl-block tilt-l">מנהל אחד</span>.
          </h1>
          <div className="svc-pain pre">{"\"הכל תקוע עלינו.\nכל דבר עובר דרך אדם אחד.\""}</div>
          <div className="svc-lede pre">{'פוקוס מוד נכנסת לפרויקטים מורכבים.\nמפרקת את המציאות.\nבונה תשתית עבודה נכונה.\nמובילה מעבר לעבודה מסודרת.\nניתן לבחור באחד משני מודלים של עבודה.'}</div>
          <a className="svc-cta" href={WA_URL} target="_blank" rel="noopener noreferrer">
            <WhatsAppGlyph size={18} />
            3 משפטים בוואטסאפ
            <ArrowL size={14} />
          </a>
        </div>
      </section>

      <section className="pm-models">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>שני מודלים</div>
            <h2 className="svc-h2 pre">{'בוחרים איך עובדים יחד.\nלפני שמתחילים.'}</h2>
          </div>

          <div className="pm-toggle reveal" role="tablist">
            <button role="tab" aria-selected={mode === 'turnkey'} className={'pm-toggle-btn' + (mode === 'turnkey' ? ' is-on' : '')} onClick={() => setMode('turnkey')}>
              <span className="pm-toggle-num">01</span>
              <span className="pm-toggle-name">מודל מלא</span>
              <span className="pm-toggle-sub">פוקוס מוד מנהלת הכל</span>
            </button>
            <button role="tab" aria-selected={mode === 'transparent'} className={'pm-toggle-btn' + (mode === 'transparent' ? ' is-on' : '')} onClick={() => setMode('transparent')}>
              <span className="pm-toggle-num">02</span>
              <span className="pm-toggle-name">מודל שקוף</span>
              <span className="pm-toggle-sub">הלקוח מנהל קדימה</span>
            </button>
          </div>

          <article className="pm-model-card">
            <header className="pm-model-head">
              <div className="pm-model-name">{active.name}</div>
              <h3 className="pm-model-sub pre">{active.sub}</h3>
            </header>
            <ul className="pm-model-bullets">
              {active.bullets.map((b, i) => <li key={i}><span className="pm-bullet-dot"></span>{b}</li>)}
            </ul>
            <div className="pm-model-fit">
              <span className="pm-model-fit-label">מתאים ל</span>
              <p className="pre">{active.fit}</p>
            </div>
          </article>

          <div className="pm-vis reveal" aria-hidden="true">
            <div className="pm-vis-row">
              <div className="pm-vis-label"><strong>מודל מלא</strong><span>פוקוס מוד = שכבת ניהול בודדת</span></div>
              <div className="pm-vis-flow">
                <span className="pm-vis-node pm-vis-node-client">לקוח</span>
                <span className="pm-vis-arrow">→</span>
                <span className="pm-vis-node pm-vis-node-fm">פוקוס מוד</span>
                <span className="pm-vis-arrow">→</span>
                <div className="pm-vis-cluster">
                  <span className="pm-vis-node pm-vis-node-vendor">פיתוח</span>
                  <span className="pm-vis-node pm-vis-node-vendor">עיצוב</span>
                  <span className="pm-vis-node pm-vis-node-vendor">תוכן</span>
                  <span className="pm-vis-node pm-vis-node-vendor">מערכות</span>
                  <span className="pm-vis-node pm-vis-node-vendor">תפעול</span>
                </div>
              </div>
              <div className="pm-vis-note">החלטה אחת. נקודת אמת אחת. הלקוח מקבל סטטוס. לא מנהל ספקים.</div>
            </div>
            <div className="pm-vis-row">
              <div className="pm-vis-label"><strong>מודל שקוף</strong><span>פוקוס מוד = שכבת ייעוץ ותיאום</span></div>
              <div className="pm-vis-flow">
                <span className="pm-vis-node pm-vis-node-client">לקוח</span>
                <span className="pm-vis-arrow">→</span>
                <div className="pm-vis-cluster">
                  <span className="pm-vis-node pm-vis-node-vendor">פיתוח</span>
                  <span className="pm-vis-node pm-vis-node-vendor">עיצוב</span>
                  <span className="pm-vis-node pm-vis-node-vendor">תוכן</span>
                  <span className="pm-vis-node pm-vis-node-vendor">מערכות</span>
                  <span className="pm-vis-node pm-vis-node-vendor">תפעול</span>
                </div>
                <div className="pm-vis-side">
                  <span className="pm-vis-arrow pm-vis-arrow-side">↗</span>
                  <span className="pm-vis-node pm-vis-node-fm pm-vis-node-side">פוקוס מוד</span>
                </div>
              </div>
              <div className="pm-vis-note">הלקוח מנהל ישירות. פוקוס מוד יושבת בצד עם כלים, מתודולוגיות וישיבות שבועיות.</div>
            </div>
          </div>

          <div className="pm-lifecycle reveal">
            <div className="pm-lifecycle-head">
              <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>מחזור חיים של פרויקט</div>
              <h3 className="pm-lifecycle-h pre">{'בכל מודל\nאותם 5 שלבים.\nשונה רק מי מוביל כל שלב.'}</h3>
            </div>
            <div className="pm-lifecycle-grid">
              {[
                { n:'01', h:'Discovery', t:'מיפוי המציאות.\nראיונות.\nתיעוד הקיים.\nהגדרת היקף.', dur:'1-3 שבועות' },
                { n:'02', h:'Planning', t:'תוכנית עבודה.\nאבני דרך.\nבחירת ספקים.\nתקציב חתום.', dur:'2-4 שבועות' },
                { n:'03', h:'Execution', t:'ספרינטים.\nישיבות שבועיות.\nסטטוס בכתב.\nניהול סיכונים.', dur:'הרוב' },
                { n:'04', h:'Review', t:'QA.\nUAT.\nתיקונים.\nהכנה להשקה.', dur:'2-4 שבועות' },
                { n:'05', h:'Handoff', t:'תיעוד.\nהעברת ידע.\nתקופת חפיפה.\nהארגון ממשיך לבד.', dur:'2-3 שבועות' },
              ].map(s => (
                <article key={s.n} className="pm-stage">
                  <div className="pm-stage-n">{s.n}</div>
                  <h4 className="pm-stage-h">{s.h}</h4>
                  <p className="pm-stage-t pre">{s.t}</p>
                  <div className="pm-stage-dur">{s.dur}</div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pm-methods">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>שיטות עבודה</div>
            <h2 className="svc-h2 pre">{'לא קודש לשיטה אחת.\nבוחרים לפי הפרויקט.'}</h2>
            <p className="pm-methods-lede pre">{'כל שיטה היא כלי.\nכל כלי יודע לעשות משהו שאחרים לא.\nהתפקיד של מנהל פרויקט הוא לדעת מתי להוציא איזה כלי.'}</p>
          </div>
          <div className="pm-methods-grid">
            {[
              { tag:'יסודות', h:'מה זה ניהול פרויקטים', t:'4 אחריות מדידות.\nהיקף, זמן, תקציב, סיכון.\nכל השאר נגזר מהן.', href:'/concepts/pm-fundamentals' },
              { tag:'שיטה', h:'Agile', t:'להגיב, לא לחזות.\nערך לפני תוכנית.\nאנשים לפני תהליכים.', href:'/concepts/agile' },
              { tag:'שיטה', h:'Scrum', t:'ספרינטים של 2-3 שבועות.\n3 תפקידים.\n4 ישיבות.', href:'/concepts/scrum' },
              { tag:'שיטה', h:'Kanban', t:'זרימה רציפה.\nלוח ויזואלי.\nWIP מוגבל.\nמדידת זמן אמת.', href:'/concepts/kanban' },
              { tag:'שיטה', h:'Waterfall', t:'הגישה הקלאסית.\nשלב אחרי שלב.\nמתאים לרגולציה ולתשתית.', href:'/concepts/waterfall' },
              { tag:'תרבות', h:'ישיבות שעובדות', t:'3 סוגי ישיבות.\nכל אחת עם תוצר.\nאם אין החלטה\nלא הייתה צריכה לקרות.', href:'/concepts/pm-meetings' },
            ].map((m, i) => (
              <a key={i} href={m.href} className="pm-method-card reveal">
                <div className="pm-method-tag">{m.tag}</div>
                <h3 className="pm-method-h">{m.h}</h3>
                <p className="pm-method-t pre">{m.t}</p>
                <span className="pm-method-cta">למאמר המלא <ArrowL size={14} /></span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="svc-what">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>מה זה כולל</div>
            <h2 className="svc-h2">3 שכבות עבודה.</h2>
          </div>
          <div className="svc-what-grid">
            <article className="svc-what-card reveal"><div className="svc-what-num">01</div><h3 className="svc-what-h">תהליכים</h3><p className="svc-what-t pre">{'פירוק של איך הארגון עובד באמת.\nמיפוי כל הגורמים.\nזיהוי איפה דברים נופלים בין הכיסאות.'}</p></article>
            <article className="svc-what-card reveal"><div className="svc-what-num">02</div><h3 className="svc-what-h">מערכות</h3><p className="svc-what-t pre">{'החלטה אילו כלים נשארים.\nאילו הולכים.\nומה מתחבר למה.\nבלי להחליף הכל.'}</p></article>
            <article className="svc-what-card reveal"><div className="svc-what-num">03</div><h3 className="svc-what-h">אנשים</h3><p className="svc-what-t pre">{'הטמעה.\nתיעוד.\nהעברת ownership פנימה.\nהארגון יודע להמשיך לבד.'}</p></article>
          </div>
        </div>
      </section>

      <FitSection
        fitFor={['ארגון שמריץ כמה פרויקטים במקביל', 'פרויקט מורכב עם הרבה גורמים', 'אין PM פנימי, או שיש אבל הוא טובע', 'צריך מישהו שייקח אחריות רוחבית']}
        notFor={['פרויקט פיתוח טהור בלי צד תפעולי', 'ארגון שמחפש מנכ״ל אופרציות במשרה מלאה', 'מי שלא מוכן לעבודה רוחבית']}
      />

      <section className="svc-case">
        <div className="wrap">
          <article className="svc-case-card reveal">
            <div className="svc-case-eyebrow"><span className="svc-case-dot"></span>מקרה אמיתי</div>
            <h3 className="svc-case-title pre">{'ארגון.\nתשתית שירות שלמה.'}</h3>
            <p className="svc-case-body pre">{'ארגון פנה עם בקשה לבוט וואטסאפ.\nבשיחה התברר שהבעיה לא בוט.\nחסר מערך שירות לקוחות שלם.\nתהליכים שצמחו לאורך השנים בלי תכנון אחיד.\nפוקוס מוד בנתה איתם את התשתית מהשורש.\nערוצי פנייה.\nניהול תורים.\nתיעוד.\nהסלמה.\nמענה.\nהבוט היה החלק הקטן.'}</p>
          </article>
        </div>
      </section>

      <FAQSection items={[
        { q:'מה זה אומר במעשה?', a:'ישיבות שבועיות.\nסטטוס בכתב.\nמסמכי מפתח לכל גורם.\nתקשורת ישירה מול מנהל הפרויקט.' },
        { q:'מה ההבדל בין שני המודלים?', a:'במודל המלא פוקוס מוד מנהלת גם את הספקים.\nבמודל השקוף הלקוח מנהל את הספקים בעצמו ומקבל ליווי מקצועי לאורך הדרך.' },
        { q:'מה קורה כשמסיימים?', a:'תקופת חפיפה מובנית בתום הפרויקט.\nתיעוד מלא.\nהארגון יודע להמשיך גם בלי תלות חיצונית.' },
        { q:'האם זה כולל ניהול תקציב?', a:'כן.\nניהול תקציב מול ספקים, מעקב על סטיות, דיווח שוטף.\nלא כולל ניהול חשבונאות כללית של הארגון.' },
      ]} />

      <LeadFormBand
        defaultService="project-management"
        title={<>פרויקט שתקוע <span className="hl-block tilt-r">חודשים</span>?</>}
        body={'שתי שורות על מה תקוע.\nפוקוס מוד חוזרת תוך 48 שעות.'}
      />
    </main>
  );
}

// ─── WEB BUILDING ─────────────────────────────────────────────
function WebServicePage() {
  useReveal();
  return (
    <main className="svc-page" data-accent="lilac">
      <SEO title="בניית אתרים" description="פוקוס מוד בונה אתרים מאפס. תכנון, עיצוב ופיתוח תחת קורת גג אחת." path="/services/web" schema={[localBusinessSchema, buildServiceSchema({ title: 'בניית אתרים', slug: 'web', description: 'פוקוס מוד בונה אתרים מאפס. תכנון, עיצוב ופיתוח תחת קורת גג אחת.' })]} />
      <section className="svc-head">
        <div className="wrap">
          <Link className="svc-back" to="/"><ArrowL size={14} /> חזרה לדף הבית</Link>
          <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>שירות · בניית אתרים</div>
          <h1 className="svc-title">
            אתרים שמעוצבים נכון, בנויים <span className="hl-block tilt-r">להביא תוצאות</span>.
          </h1>
          <div className="svc-lede pre">{'עיצוב ופיתוח אתרים חדשים, או עיצוב מחדש לאתרים קיימים.\nאנחנו משלבים אסטרטגיה, אפיון, עיצוב ופיתוח כדי ליצור אתר שנראה מקצועי, מציג את הערך שלכם בצורה ברורה ומניע לקוחות לבצע את הפעולות שחשובות לעסק שלכם.\nאתרי תדמית, חנויות אונליין ומערכות פנימיות, מותאמים לעסק, למטרות ולקהל שלכם.'}</div>
          <a className="svc-cta" href={WA_URL} target="_blank" rel="noopener noreferrer">
            <WhatsAppGlyph size={18} />3 משפטים בוואטסאפ<ArrowL size={14} />
          </a>
        </div>
      </section>

      <section className="hm-mirror">
        <div className="wrap">
          <div className="hm-mirror-head reveal">
            <div className="hm-eyebrow"><span className="hm-eyebrow-bar"></span>אולי הגיע הזמן לרענן</div>
            <h2 className="hm-h2 hm-h2-single">אולי הגיע הזמן לרענן את האתר או העסק שלכם?</h2>
          </div>
          <div className="hm-mirror-grid">
            {[
              { t: 'האתר שלנו לא נראה כמו הרמה של העסק.', pip: 'coral' },
              { t: 'העסק התקדם, האתר נשאר מאחור.', pip: 'yellow' },
              { t: 'האתר כבר לא מייצג אותנו.', pip: 'lilac' },
              { t: 'אנחנו נראים הרבה פחות טוב ממה שאנחנו באמת.', pip: 'teal' },
              { t: 'הלקוחות מקבלים רושם לא נכון על העסק.', pip: 'coral' },
              { t: 'האתר לא משקף את מה שאנחנו יודעים לעשות.', pip: 'yellow' },
              { t: 'כשאנחנו שולחים לקוחות לאתר, אנחנו לא שלמים איתו.', pip: 'lilac' },
              { t: 'האתר כבר לא מתאים למקום שבו העסק נמצא היום.', pip: 'teal' },
            ].map((q, i) => (
              <figure key={i} className="hm-quote reveal" data-pip={q.pip}>
                <span className="hm-quote-mark" aria-hidden="true">"</span>
                <blockquote>{q.t}</blockquote>
                <span className="hm-quote-pip" aria-hidden="true"></span>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="svc-what">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>מה זה כולל</div>
            <h2 className="svc-h2 svc-h2-single">3 שכבות עבודה, תחת קורת גג אחת.</h2>
          </div>
          <div className="svc-what-grid">
            <article className="svc-what-card reveal"><div className="svc-what-num">01</div><h3 className="svc-what-h">תכנון ואפיון</h3><ul className="svc-ul"><li>מי הקהל</li><li>מה התוצאה</li><li>איך הוא צריך להרגיש</li><li>מה ההבדל מהמתחרים</li><li>לא מתחילים בעיצוב</li></ul></article>
            <article className="svc-what-card reveal"><div className="svc-what-num">02</div><h3 className="svc-what-h">עיצוב</h3><ul className="svc-ul"><li>עברית RTL מההתחלה</li><li>מובייל קודם</li><li>טיפוגרפיה, צבע ומערכת חזותית עקבית</li><li>לא תבנית</li></ul></article>
            <article className="svc-what-card reveal"><div className="svc-what-num">03</div><h3 className="svc-what-h">פיתוח</h3><ul className="svc-ul"><li>קוד אמיתי</li><li>מהיר</li><li>נגיש</li><li>SEO תקין</li><li>כל מה שנבנה נבדק לפני שהוא עולה</li><li>כלי AI עוזרים בקצב, לא מחליפים שיקול דעת</li></ul></article>
          </div>
        </div>
      </section>

      <section className="svc-subs">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>סוגי אתרים</div>
            <h2 className="svc-h2">איזה אתר אתם צריכים?</h2>
          </div>
          <div className="svc-subs-grid">
            <article className="svc-sub reveal"><h3 className="svc-sub-h">אתר תדמית</h3><ul className="svc-ul"><li>דף בית, אודות, שירותים, יצירת קשר</li><li>הכרטיס הראשון של העסק לעולם</li><li>תוכן עמוק, מבנה ברור, חוויית קריאה נקייה</li></ul></article>
            <article className="svc-sub reveal"><h3 className="svc-sub-h">אתר תוכן</h3><ul className="svc-ul"><li>בלוג, מאגר ידע, מדריכים</li><li>תשתית CMS שמאפשרת לכתוב בלי מפתח</li><li>SEO מובנה</li><li>מהירות טעינה גבוהה</li></ul></article>
            <article className="svc-sub reveal"><h3 className="svc-sub-h">חנות מקוונת</h3><ul className="svc-ul"><li>קטלוג מוצרים</li><li>עגלה ותשלום</li><li>ניהול הזמנות</li><li>חיבור לסליקה, למשלוחים ולהנהלת חשבונות</li></ul></article>
            <article className="svc-sub reveal"><h3 className="svc-sub-h">פאנל פנימי</h3><ul className="svc-ul"><li>מערכת לניהול פנימי</li><li>הרשאות</li><li>דוחות</li><li>תפעול שוטף</li><li>מותאם בדיוק למה שהארגון צריך</li></ul></article>
            <article className="svc-sub reveal"><h3 className="svc-sub-h">אתר ארגוני גדול</h3><ul className="svc-ul"><li>עשרות עד מאות עמודים</li><li>ריבוי בעלי תוכן</li><li>הרשאות</li><li>ריבוי שפות</li><li>תחזוקה לאורך זמן</li></ul></article>
            <article className="svc-sub reveal svc-sub-link">
              <h3 className="svc-sub-h">דפי נחיתה</h3>
              <ul className="svc-ul"><li>שירות נפרד</li><li>דפים ממוקדים לקמפיין או השקה</li><li>תכלית אחת לכל דף</li></ul>
              <Link to="/services/landing-pages" className="svc-sub-cta">לעמוד דפי נחיתה <ArrowL size={14} /></Link>
            </article>
          </div>
        </div>
      </section>

      <section className="svc-tech">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>הסטאק</div>
            <h2 className="svc-h2">מה רץ מאחורי הקלעים.</h2>
          </div>
          <div className="svc-tech-grid">
            {[['פיתוח','React, Next.js, Astro, TypeScript'],['CMS','Sanity, Strapi, Contentful'],['תשתית','Vercel, Cloudflare, Supabase'],['AI Tooling','Claude Code, Cursor, Anthropic API']].map(([l,v],i) => (
              <div key={i} className="svc-tech-row reveal"><div className="svc-tech-label">{l}</div><div className="svc-tech-val">{v}</div></div>
            ))}
          </div>
        </div>
      </section>

      <section className="svc-case">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>אתרים שבנינו</div>
          </div>
        </div>
        <article className="svc-case-card svc-case-row reveal">
          <div className="svc-case-text">
            <h3 className="svc-case-title">FlyMoments</h3>
            <p className="svc-case-body pre">{'חברת חוויות MICE\nכנסים, אירועים תאגידיים ואינסנטיב\nשיתוף פעולה מאז 2017\nכשהיה צריך אתר חדש, הייתה רק שיחה אחת.'}</p>
            <a className="svc-case-link" href="https://flymoments.com" target="_blank" rel="noopener noreferrer">flymoments.com <ArrowL size={14} /></a>
          </div>
          <img src="/flymoments-screenshot.webp" alt="flymoments.com" className="svc-case-img" loading="eager" />
        </article>
        <div className="wrap">
          <div className="svc-case-cards svc-eden-gap">
            <article className="svc-case-card reveal">
              <div className="svc-case-text">
                <h3 className="svc-case-title">Eden.A</h3>
                <p className="svc-case-body pre">{'יוצרת תוכן סושיאל.\nרילס, ימי צילום, עריכה.\nאתר מלא: בית, אודות, שירותים, עבודות, יצירת קשר.'}</p>
                <a className="svc-case-link" href="/sites/eden/index.html" target="_blank" rel="noopener noreferrer">edenavitbul.com <ArrowL size={14} /></a>
              </div>
              <img src="/assets/eden-a-hero.png" alt="Eden.A" className="svc-case-img" loading="lazy" />
            </article>
          </div>
        </div>
      </section>

      <FAQSection items={[
        { q:'מי כותב את הטקסטים?', a:'FocusMode מציעה גם שירותי קופי ועריכה בנוסף אך אתם לא מחויבים לשירות זה.\nהקול של הארגון נשאר אצל הארגון.' },
        { q:'האם זה כולל קידום?', a:'SEO טכני נכון, כן.\nניהול קמפיינים, ייעוץ שיווקי או ניהול חברתי, לא כלולים.' },
        { q:'מה קורה אחרי שהאתר עולה לאוויר?', a:'תקופת ליווי מובנית להתאמות קטנות וכיוונים.\nלאחר מכן ניתן לבחור בתחזוקה שוטפת או לתפעל את האתר באופן עצמאי.' },
        { q:'האם אקבל גישה לקוד?', a:'כן.\nכל הקוד והנכסים הם בבעלות העסק/הארגון ועוברים אליכם בסוף הפרויקט.\nאין מחויבות ל-FocusMode.' },
      ]} />

      <LeadFormBand
        defaultService="web"
        title="למה שלא נכיר ונדבר?"
        body={'שתי שורות עליכם, הקהל ועל המטרה.\nשיחת היכרות ללא התחייבות.'}
      />
    </main>
  );
}

// ─── LANDING PAGES ────────────────────────────────────────────
function LandingPagesServicePage() {
  useReveal();
  return (
    <main className="svc-page" data-accent="coral">
      <SEO title="דפי נחיתה" description="דף נחיתה לא אתר. תכלית אחת. מסר אחד. פעולה אחת. פוקוס מוד בונה דפי נחיתה שמחוברים למערכת המעקב והלידים." path="/services/landing-pages" schema={[localBusinessSchema, buildServiceSchema({ title: 'דפי נחיתה', slug: 'landing-pages', description: 'דף נחיתה לא אתר. תכלית אחת. מסר אחד. פעולה אחת.' })]} />
      <section className="svc-head">
        <div className="wrap">
          <Link className="svc-back" to="/"><ArrowL size={14} /> חזרה לדף הבית</Link>
          <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>שירות · דפי נחיתה</div>
          <h1 className="svc-title">דף נחיתה.<br/><span className="hl-block tilt-l">תכלית אחת</span>.</h1>
          <div className="svc-pain pre">{'\"צריך להעלות קמפיין.\nולא יודעים מאיפה מתחילים.\"'}</div>
          <div className="svc-lede pre">{'דף נחיתה לא אתר.\nתכלית אחת.\nמסר אחד.\nפעולה אחת.\nפוקוס מוד בונה דפי נחיתה שמחוברים למערכת המעקב והלידים.'}</div>
          <a className="svc-cta" href={WA_URL} target="_blank" rel="noopener noreferrer">
            <WhatsAppGlyph size={18} />3 משפטים בוואטסאפ<ArrowL size={14} />
          </a>
        </div>
      </section>

      <section className="svc-what">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>מה זה כולל</div>
            <h2 className="svc-h2">דף מוכן לעלות לקמפיין.</h2>
          </div>
          <div className="svc-what-grid">
            <article className="svc-what-card reveal"><div className="svc-what-num">01</div><h3 className="svc-what-h">קופי</h3><p className="svc-what-t pre">{'כותרת.\nסאב.\nגוף.\nCTA.\nכל מילה עובדת לתכלית הדף.'}</p></article>
            <article className="svc-what-card reveal"><div className="svc-what-num">02</div><h3 className="svc-what-h">עיצוב</h3><p className="svc-what-t pre">{'מובייל קודם.\nגרירה לטופס במקום אחד.\nללא הסחות.\nללא ניווט מיותר.'}</p></article>
            <article className="svc-what-card reveal"><div className="svc-what-num">03</div><h3 className="svc-what-h">תשתית</h3><p className="svc-what-t pre">{'חיבור לטופס לידים.\nMeta Pixel.\nGoogle Tag Manager.\nאירועי המרה.\nכל מה שצריך כדי שהקמפיין יעבוד.'}</p></article>
          </div>
        </div>
      </section>

      <section className="svc-subs">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>סוגי דפים</div>
            <h2 className="svc-h2">לפי מטרת הקמפיין.</h2>
          </div>
          <div className="svc-subs-grid">
            <article className="svc-sub reveal"><h3 className="svc-sub-h">דף לידים</h3><p className="svc-sub-t pre">{'איסוף פרטים לליד חם.\nטופס קצר.\nהוכחות חברתיות.\nתשובות לחסמים נפוצים.'}</p></article>
            <article className="svc-sub reveal"><h3 className="svc-sub-h">דף השקה</h3><p className="svc-sub-t pre">{'מוצר חדש או שירות חדש.\nסיפור.\nתועלות.\nרשימת המתנה או רכישה ישירה.'}</p></article>
            <article className="svc-sub reveal"><h3 className="svc-sub-h">דף אירוע</h3><p className="svc-sub-t pre">{'וובינר.\nכנס.\nמסיבת השקה.\nתאריך, מקום, הרשמה.'}</p></article>
            <article className="svc-sub reveal"><h3 className="svc-sub-h">דף מוצר</h3><p className="svc-sub-t pre">{'מוצר ספציפי במסגרת קמפיין.\nמפרט.\nתועלות.\nכפתור רכישה.'}</p></article>
          </div>
        </div>
      </section>

      <FitSection
        fitFor={['קמפיין ממומן או השקה','מוצר או שירות עם מסר ממוקד','תקציב מדיה שעובד כבר או בקרוב','צוות שיווק שיודע מה הוא רוצה']}
        notFor={['אתר מלא של כמה עמודים.\nלזה יש שירות נפרד','מי שעדיין לא יודע מי הקהל','דף שצריך להיות ולא ברור למה']}
      />

      <section className="lp-examples">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>דוגמאות חיות</div>
            <h2 className="svc-h2">3 קמפיינים. 3 קהלים.<br/><span className="hl-block tilt-r">אותה שיטה</span>.</h2>
            <p className="lp-examples-lede pre">{'כל דף נבנה לקהל ספציפי.\nכל אחד עם תכלית אחת.'}</p>
          </div>
          <div className="lp-examples-grid">
            <a className="lp-card reveal" href="/landings/law.html" target="_blank" rel="noopener noreferrer">
              <div className="lp-card-frame" aria-hidden="true">
                <div className="lp-card-chrome"><span></span><span></span><span></span><em>landing-law.html</em></div>
                <div className="lp-card-shot-wrap"><img src="/assets/lp-law.png" alt="דף נחיתה משפטי" className="lp-card-shot" loading="lazy" /></div>
              </div>
              <div className="lp-card-meta"><div><div className="lp-card-tag">משפטי</div><h3>צוואות וייפוי כוח</h3><p>אמון, רוגע, דיסקרטיות. טופס ליד דיסקרטי. CTA: ייעוץ ראשוני.</p></div><span className="lp-card-arrow"><ArrowL size={16} /></span></div>
            </a>
            <a className="lp-card reveal" href="/landings/real-estate.html" target="_blank" rel="noopener noreferrer">
              <div className="lp-card-frame" aria-hidden="true">
                <div className="lp-card-chrome"><span></span><span></span><span></span><em>landing-real-estate.html</em></div>
                <div className="lp-card-shot-wrap"><img src="/assets/lp-realestate.png" alt="דף נחיתה נדל״ן" className="lp-card-shot" loading="lazy" /></div>
              </div>
              <div className="lp-card-meta"><div><div className="lp-card-tag">נדל״ן</div><h3>פרויקט יוקרה — צפון נתניה</h3><p>סטטוס, יוקרה, מקום. לוקיישן, מפרט, דירה לדוגמה. CTA: תיאום פגישה.</p></div><span className="lp-card-arrow"><ArrowL size={16} /></span></div>
            </a>
            <a className="lp-card reveal" href="/landings/yoga.html" target="_blank" rel="noopener noreferrer">
              <div className="lp-card-frame" aria-hidden="true">
                <div className="lp-card-chrome"><span></span><span></span><span></span><em>landing-yoga.html</em></div>
                <div className="lp-card-shot-wrap"><img src="/assets/lp-yoga.png" alt="דף נחיתה סטודיו יוגה" className="lp-card-shot" loading="lazy" /></div>
              </div>
              <div className="lp-card-meta"><div><div className="lp-card-tag">בריאות</div><h3>סטודיו יוגה · שיעור ניסיון</h3><p>חמימות, רוגע, נגישות. שיעור ניסיון, סוגי שיעורים, צוות. CTA: הרשמה.</p></div><span className="lp-card-arrow"><ArrowL size={16} /></span></div>
            </a>
          </div>
          <p className="lp-examples-note reveal">דפים אלו הם דמו. נבנים מותאמים אישית לכל קמפיין, בשפת המותג ובמסר הנכון לקהל.</p>
        </div>
      </section>

      <FAQSection items={[
        { q:'מה ההבדל בין דף נחיתה לאתר?', a:'אתר משרת הרבה מטרות.\nתדמית, מידע, יצירת קשר, בלוג.\nדף נחיתה משרת מטרה אחת.\nליד או רכישה.\nכל פיקסל בדף תורם לאותה מטרה.' },
        { q:'האם הדף יישב באותו דומיין?', a:'ניתן.\nאו לחילופין על דומיין משני, או על שירות נחיתה ייעודי כמו Vercel.\nהבחירה תלויה במטרת הקמפיין.' },
        { q:'מה לגבי בדיקות A/B?', a:'ניתן להגדיר מראש מבנה לשתי וריאציות.\nהמעקב נעשה דרך כלי ניתוח חיצוני.' },
        { q:'האם זה כולל ניהול קמפיין?', a:'לא.\nפוקוס מוד בונה את הדף ואת התשתית.\nניהול הקמפיין הוא תפקיד נפרד.\nניתן להמליץ על אנשי מקצוע.' },
      ]} />

      <LeadFormBand
        defaultService="landing-pages"
        title={<>קמפיין שמחכה ל<span className="hl-block tilt-r">דף נחיתה</span>?</>}
        body={'מה הקמפיין.\nמה הקהל.\nמתי עולה.'}
      />
    </main>
  );
}

// ─── SYSTEMS ──────────────────────────────────────────────────
function SystemsServicePage() {
  useReveal();
  return (
    <main className="svc-page" data-accent="teal">
      <SEO title="מערכות ואינטגרציות" description="פוקוס מוד מחברת מערכות קיימות. בלי להחליף מה שעובד." path="/services/systems" schema={[localBusinessSchema, buildServiceSchema({ title: 'מערכות ואינטגרציות', slug: 'systems', description: 'פוקוס מוד מחברת מערכות קיימות. בלי להחליף מה שעובד.' })]} />
      <section className="svc-head">
        <div className="wrap">
          <Link className="svc-back" to="/"><ArrowL size={14} /> חזרה לדף הבית</Link>
          <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>שירות · מערכות ואינטגרציות</div>
          <h1 className="svc-title">3 כלים שונים?<br/>שיתחילו <span className="hl-block tilt-l">לדבר</span>.</h1>
          <div className="svc-pain pre">{'\"כל דבר עובר ידנית.\nאנחנו עושים מיליון העתק-הדבק.\"'}</div>
          <div className="svc-lede pre">{'CRM קיים.\nשרת מיילים קיים.\nGoogle Drive, תוכנת חשבונות, מערכת ניהול.\nוכלום מתחבר.\nפוקוס מוד מחברת ביניהם.\nאו בונה את החלק החסר.\nבלי להחליף את מה שעובד.'}</div>
          <a className="svc-cta" href={WA_URL} target="_blank" rel="noopener noreferrer">
            <WhatsAppGlyph size={18} />3 משפטים בוואטסאפ<ArrowL size={14} />
          </a>
        </div>
      </section>

      <section className="svc-what">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>מה זה כולל</div>
            <h2 className="svc-h2">3 שכבות.</h2>
          </div>
          <div className="svc-what-grid">
            <article className="svc-what-card reveal"><div className="svc-what-num">01</div><h3 className="svc-what-h">מיפוי</h3><p className="svc-what-t pre">{'מה נכנס מאיפה.\nמה צריך לזרום לאן.\nמה נשאר ידני, מה לא.'}</p></article>
            <article className="svc-what-card reveal"><div className="svc-what-num">02</div><h3 className="svc-what-h">חיבור</h3><p className="svc-what-t pre">{'פיתוח אינטגרציות.\nגשרים בין כלים.\nתהליכים שעוברים בלי שמישהו צריך להעתיק.'}</p></article>
            <article className="svc-what-card reveal"><div className="svc-what-num">03</div><h3 className="svc-what-h">תיעוד</h3><p className="svc-what-t pre">{'כל חיבור מתועד.\nהצוות יודע איך זה עובד.\nכשצריך לשנות, יש למה לחזור.'}</p></article>
          </div>
        </div>
      </section>

      <section className="svc-examples">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>דוגמאות</div>
            <h2 className="svc-h2">לפני. אחרי.</h2>
          </div>
          <div className="svc-ex-list">
            {[
              { h:'פורטל לקוחות', b:'לקוח שואל סטטוס במייל.\nנציג בודק ב-3 מקומות.\nחוזר בעוד יום.', a:'פורטל מחובר ל-CRM.\nלקוח רואה סטטוס בזמן אמת.\nנציג מטפל רק במה שצריך אדם.' },
              { h:'חתימת חוזים', b:'PDF נשלח במייל.\nמודפס.\nנחתם.\nנסרק.\nנשלח חזרה.\nשבוע עובר.', a:'חתימה דיגיטלית.\nסבבי אישורים אוטומטיים.\nתיעוד מלא.\nשעות במקום שבוע.' },
              { h:'קופונים וקמפיינים', b:'קמפיין SMS ידני.\nרשימות באקסל.\nכפילויות.\nשגיאות שליחה.\nיום עבודה למבצע.', a:'מערכת מחוברת.\nקופון נוצר אוטומטית.\nשליחה מוגדרת.\nדוחות בזמן אמת.' },
            ].map((ex, i) => (
              <article key={i} className="svc-ex reveal">
                <h3 className="svc-ex-h">{ex.h}</h3>
                <div className="svc-ex-cols">
                  <div className="svc-ex-col svc-ex-before"><div className="svc-ex-label">לפני</div><p className="pre">{ex.b}</p></div>
                  <div className="svc-ex-arrow">←</div>
                  <div className="svc-ex-col svc-ex-after"><div className="svc-ex-label">אחרי</div><p className="pre">{ex.a}</p></div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <FitSection
        fitFor={['עסק שכבר עובד עם כמה מערכות במקביל','כבר קיימות 2-3 מערכות מרכזיות','צוות מבזבז זמן על העתק-הדבק','תהליכים פנימיים שלא רצים']}
        notFor={['מי שעדיין לא בחר מערכת ליבה','מי שמחפש להחליף הכל מאפס','פרויקט שדורש 5 מפתחים במקביל']}
      />

      <section className="svc-case">
        <div className="wrap">
          <article className="svc-case-card reveal">
            <div className="svc-case-eyebrow"><span className="svc-case-dot"></span>מקרה אמיתי</div>
            <h3 className="svc-case-title">מערכת חתימות וסבבי אישורים</h3>
            <p className="svc-case-body pre">{'ארגון עם תהליך אישורים מסורבל.\nהדפסות.\nחתימות פיזיות.\nסבבים שנעלמים.\nפוקוס מוד בנתה מערכת דיגיטלית.\nהיררכיית אישורים לפי תפקיד וסכום.\nתיעוד אוטומטי.\nהתראות.\nסטטוס בזמן אמת.\nתהליך של שבועות הפך לימים.'}</p>
          </article>
        </div>
      </section>

      <FAQSection items={[
        { q:'אצטרך להחליף את המערכות הקיימות?', a:'ברוב המקרים לא.\nרוב מערכות הליבה ניתנות לחיבור.\nאם מערכת ישנה מאוד ולא ניתנת לחיבור, ייבחן פתרון נקודתי.' },
        { q:'מה קורה אם משהו תוקע?', a:'כל אינטגרציה מתועדת.\nיש מנגנון fallback.\nתקלה מתגלה בתוך דקות, לא בעוד שבוע.' },
        { q:'מי מתחזק אחרי שמסיימים?', a:'תקופת ליווי מובנית.\nתיעוד מלא לכל מי שייכנס לתחזוקה.\nניתן להמשיך בריטיינר אם יש בו ערך.' },
        { q:'מה לגבי אבטחת מידע?', a:'כל חיבור עובר בתקני התעשייה.\nהרשאות מינימליות.\nתיעוד גישה.\nלוגים.' },
      ]} />

      <LeadFormBand
        defaultService="systems"
        title={<>מערכות ש<span className="hl-block tilt-r">לא מדברות</span>?</>}
        body={'אילו מערכות קיימות.\nמה לא עובד.\nמה תקוע באמצע.'}
      />
    </main>
  );
}

// ─── AUTOMATIONS ──────────────────────────────────────────────
function AutomationsServicePage() {
  useReveal();
  return (
    <main className="svc-page" data-accent="coral">
      <SEO title="אוטומציות" description="פוקוס מוד בונה אוטומציות שמחזירות שעות לעסק. תהליך שחוזר, רץ לבד." path="/services/automations" schema={[localBusinessSchema, buildServiceSchema({ title: 'אוטומציות', slug: 'automations', description: 'פוקוס מוד בונה אוטומציות שמחזירות שעות לעסק. תהליך שחוזר, רץ לבד.' })]} />
      <section className="svc-head">
        <div className="wrap">
          <Link className="svc-back" to="/"><ArrowL size={14} /> חזרה לדף הבית</Link>
          <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>שירות · אוטומציות</div>
          <h1 className="svc-title">תהליך חוזר 5 פעמים בשבוע?<br/><span className="hl-block tilt-r">בונים אותו פעם אחת</span>.</h1>
          <div className="svc-pain pre">{'\"אני עושה את אותו דבר ידנית.\nכל יום.\"'}</div>
          <div className="svc-lede pre">{'עד 16 שעות בשבוע הולכות על משימות אדמיניסטרטיביות חוזרות.\nאוטומציה מחזירה את הזמן הזה לעבודה האמיתית.\nבלי שאף אחד יצטרך לזכור.'}</div>
          <a className="svc-cta" href={WA_URL} target="_blank" rel="noopener noreferrer">
            <WhatsAppGlyph size={18} />3 משפטים בוואטסאפ<ArrowL size={14} />
          </a>
        </div>
      </section>

      <section className="svc-what">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>תהליכים שאפשר להריץ לבד</div>
            <h2 className="svc-h2">3 דוגמאות חיות.</h2>
          </div>
          <div className="svc-what-grid">
            <article className="svc-what-card reveal"><div className="svc-what-num">01</div><h3 className="svc-what-h">ליד נכנס</h3><p className="svc-what-t pre">{'ליד מהאתר.\nתיעוד ב-CRM.\nתגובה אוטומטית.\nמשימה לצוות.\nFollow-up אם אין מענה.\nבלי שמישהו יצטרך לזכור.'}</p></article>
            <article className="svc-what-card reveal"><div className="svc-what-num">02</div><h3 className="svc-what-h">לקוח חדש</h3><p className="svc-what-t pre">{'הצטרפות לקוח.\nמייל ברוכים הבאים.\nפתיחת תיקייה.\nשליחת מסמכים.\nתיאום פגישה.\nעדכון הצוות.\nהכל אוטומטי.'}</p></article>
            <article className="svc-what-card reveal"><div className="svc-what-num">03</div><h3 className="svc-what-h">חשבונית</h3><p className="svc-what-t pre">{'הפקת חשבונית.\nשליחה.\nתזכורת תשלום אחרי 7 ימים.\nתזכורת נוספת אחרי 14 ימים.\nעדכון סטטוס כשהתקבל.\nבלי לעקוב ידנית.'}</p></article>
          </div>
        </div>
      </section>

      <FitSection
        fitFor={['עסק שעובד עם 3+ כלים שונים','לידים, לקוחות, מסמכים, תזכורות חוזרים','תחושה שהעסק עובד קשה מדי על דברים קטנים','מוכנים לתת לתהליך לרוץ לבד']}
        notFor={['תהליך שעדיין לא ברור איך הוא עובד','פעולה שקורית פעם בחודש','מצב שצריך לפני הכל סדר תפעולי']}
      />

      <section className="svc-case">
        <div className="wrap">
          <article className="svc-case-card reveal">
            <div className="svc-case-eyebrow"><span className="svc-case-dot"></span>מקרה אמיתי</div>
            <h3 className="svc-case-title">ניהול קופונים והודעות SMS</h3>
            <p className="svc-case-body pre">{'לקוח עם פעילות שיווק ב-SMS.\nכל שליחה הייתה ידנית.\nבדיקת קופונים, התאמה למספר, שליחה, מעקב, ניקוי כפילויות.\nכל מבצע גזל יום עבודה.\nאחרי האוטומציה, כל קמפיין מתבצע מעצמו.\nהצוות עוסק בלתכנן.\nלא בלבצע.'}</p>
          </article>
        </div>
      </section>

      <FAQSection items={[
        { q:'מה קורה אם משהו נשבר?', a:'כל אוטומציה כוללת התראה כשמשהו נכשל.\nתקלה ידועה תוך דקות.\nלא מתגלה אחרי שבוע.' },
        { q:'אצטרך לדעת לקודד?', a:'לא.\nהכל מוקם עליי.\nהארגון מקבל תיעוד ברור.\nאפשר לשנות פרטים קטנים בעצמכם.\nלשינויים מורכבים, נדבר.' },
        { q:'מה ההבדל בין אוטומציה ל-AI flow?', a:'אוטומציה רצה לפי חוקים קבועים.\nAI flow מוסיף שכבת ניתוח.\nסיווג טקסט חופשי, סיכומים, הבנת הקשר.\nאותו עיקרון: שעות חוזרות לעסק.' },
        { q:'באילו כלים אתם משתמשים?', a:'Make, n8n, Zapier, וכלי AI.\nבחירת הכלי תלויה במורכבות התהליך ובמערכות הקיימות.' },
      ]} />

      <LeadFormBand
        defaultService="automations"
        title={<>תהליך שגוזל <span className="hl-block tilt-r">שעות</span>?</>}
        body={'איזה תהליך, כמה זמן הוא לוקח.\nמה המערכות שבדרך.'}
      />
    </main>
  );
}

// ─── CONSULTING ───────────────────────────────────────────────
function ConsultingServicePage() {
  useReveal();
  return (
    <main className="svc-page" data-accent="lilac">
      <SEO title="ייעוץ ומיפוי תהליכים" description="ייעוץ דרכי עבודה. איך משימה זזה. איך החלטה מתקבלת. מיפוי ומפת דרכים." path="/services/consulting" schema={[localBusinessSchema, buildServiceSchema({ title: 'ייעוץ ומיפוי תהליכים', slug: 'consulting', description: 'ייעוץ דרכי עבודה. איך משימה זזה. איך החלטה מתקבלת.' })]} />
      <section className="svc-head">
        <div className="wrap">
          <Link className="svc-back" to="/"><ArrowL size={14} /> חזרה לדף הבית</Link>
          <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>שירות · ייעוץ ומיפוי תהליכים</div>
          <h1 className="svc-title">כל אחד בארגון<br/>עובד <span className="hl-block tilt-l">אחרת</span>.</h1>
          <div className="svc-pain pre">{'\"גדלנו מהר.\nאין שיטה.\nכל יום מתחיל מאפס.\"'}</div>
          <div className="svc-lede pre">{'זה לא ייעוץ טכנולוגי.\nזה ייעוץ דרכי עבודה.\nאיך מנהל מעביר משימה.\nאיך נראית ישיבת צוות שעובדת.\nאיך מגייסים את האדם הנכון.\nאיך מגדירים תפקיד.\nאיך הארגון רץ כשהמנכ״ל לא בפנים.'}</div>
          <a className="svc-cta" href={WA_URL} target="_blank" rel="noopener noreferrer">
            <WhatsAppGlyph size={18} />3 משפטים בוואטסאפ<ArrowL size={14} />
          </a>
        </div>
      </section>

      <section className="svc-context">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>מה זה לא</div>
            <h2 className="svc-h2 pre">{'קודם כל.\nמה זה לא.'}</h2>
          </div>
          <div className="svc-context-grid">
            <div className="svc-context-item reveal"><span className="svc-context-x">×</span><p className="pre">{'לא בחירת CRM.\nלא הטמעת מערכת.\nלא אינטגרציה בין כלים.\nזה שירות נפרד.'}</p></div>
            <div className="svc-context-item reveal"><span className="svc-context-x">×</span><p className="pre">{'לא קמפיין שיווקי.\nלא ייעוץ פיננסי.\nלא בניית אסטרטגיה עסקית.'}</p></div>
            <div className="svc-context-item reveal"><span className="svc-context-x">×</span><p className="pre">{'לא ביקורת.\nלא דירוג עובדים.\nלא חוות דעת על אנשים.'}</p></div>
          </div>
          <p className="svc-context-foot pre reveal">{'זה מיפוי איך אנשים עובדים אחד מול השני.\nאיך משימה זזה.\nאיך החלטה מתקבלת.\nאיך הצוות מדבר.\nאיך נראה יום עבודה רגיל.'}</p>
        </div>
      </section>

      <section className="svc-areas">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>מה ממפים</div>
            <h2 className="svc-h2 pre">{'8 שטחי עבודה.\nכל אחד שאלה אחת.'}</h2>
          </div>
          <div className="svc-areas-grid">
            {[
              { n:'01', h:'הגדרת תפקיד', q:'מה בדיוק האדם הזה עושה?\nמה לא מתפקידו?\nמה גבולות האחריות?' },
              { n:'02', h:'יום עבודה', q:'מה קורה ב-9.\nב-12.\nב-17.\nאילו פעולות חוזרות.\nאילו דפוסים קבועים.' },
              { n:'03', h:'משימות', q:'איך מנהל מעביר משימה.\nאיך מבקשים סטטוס.\nמתי משימה נחשבת סגורה.' },
              { n:'04', h:'ישיבות', q:'ישיבת מנכ״ל.\nישיבת מנהלים.\nישיבת צוות.\nמי, על מה, באיזה קצב, עם מה יוצאים.' },
              { n:'05', h:'החלטות', q:'מי מחליט מה.\nמתי מערבים את המנכ״ל.\nמה דורש אישור.\nמה לא.' },
              { n:'06', h:'גיוס וקליטה', q:'איך כותבים מודעה.\nאיך מראיינים.\nאיך קולטים עובד חדש.\nמתי הוא עצמאי.' },
              { n:'07', h:'הסלמות', q:'מתי משימה הולכת למעלה.\nמה עושים עם לקוח קשה.\nאיך מטפלים בתקלה דחופה.' },
              { n:'08', h:'ידע פנימי', q:'איפה דברים כתובים.\nמה רק בראש של אנשים.\nמה יקרה אם מישהו עוזב.' },
            ].map(a => (
              <article key={a.n} className="svc-area reveal">
                <div className="svc-area-num">{a.n}</div>
                <h3 className="svc-area-h">{a.h}</h3>
                <p className="svc-area-q pre">{a.q}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="svc-signals">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>מתי קוראים לפוקוס מוד</div>
            <h2 className="svc-h2 pre">{'5 משפטים.\nאם משהו מזה נשמע מוכר.'}</h2>
          </div>
          <ul className="svc-signals-list">
            <li className="reveal pre">{'“פעם ידעתי הכל.\nעכשיו אני לא יודע מה קורה.”'}</li>
            <li className="reveal pre">{'“עובדים ותיקים אומרים, פעם זה לא היה ככה.”'}</li>
            <li className="reveal pre">{'“חדשים נקלטים ולא יודעים מאיפה להתחיל.”'}</li>
            <li className="reveal pre">{'“ישיבות מתפזרות.\nאף אחד לא יוצא עם משהו ברור.”'}</li>
            <li className="reveal pre">{'“המייל שלי מתפוצץ.\nכל דבר עובר דרכי.”'}</li>
          </ul>
        </div>
      </section>

      <section className="svc-flow">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>איך עובדים</div>
            <h2 className="svc-h2">4 שלבים.</h2>
          </div>
          <div className="svc-flow-list">
            {[
              { n:'01', h:'שיחות עומק', b:'5 עד 10 שיחות אישיות.\nמנכ״ל, מנהלי מחלקות, עובדים ותיקים, חדשים.\nכל אחד רואה משהו אחר.\nכל אחד יודע משהו אחר.' },
              { n:'02', h:'צפייה ביומיום', b:'לא מצגות.\nשבוע עבודה רגיל.\nלראות איך משימה זזה באמת.\nאיך פגישה נראית באמת.\nאיפה דברים נתקעים באמת.' },
              { n:'03', h:'דוח שיקוף', b:'מסמך אחד.\nמה ראינו.\nאיפה החיכוך.\nמה דחוף.\nמה אפשר לדחות.\nבכבוד, בלי לרכך.' },
              { n:'04', h:'מפת דרכים', b:'ויזואלית.\nכל שטח עבודה, מה לתקן.\nבאיזה סדר.\nולמה.\nרשימת פעולות בסדר עדיפויות.' },
            ].map(s => (
              <article key={s.n} className="svc-flow-step reveal">
                <div className="svc-flow-n">{s.n}</div>
                <div className="svc-flow-body">
                  <h3 className="svc-flow-h">{s.h}</h3>
                  <p className="pre">{s.b}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="svc-deliv">
        <div className="wrap">
          <div className="svc-section-head reveal">
            <div className="svc-eyebrow"><span className="svc-eyebrow-bar"></span>מה הארגון מקבל</div>
            <h2 className="svc-h2">4 תוצרים.</h2>
          </div>
          <div className="svc-deliv-grid">
            {[
              { n:'01', h:'מסמך שיקוף', b:'PDF מסודר.\nמה ראינו.\nאיפה הפערים.' },
              { n:'02', h:'מפת תפקידים', b:'מי עושה מה.\nגבולות אחריות.\nאיפה חופפים.' },
              { n:'03', h:'נהלי עבודה', b:'SOP בסיסי.\nתהליכי מפתח כתובים.\nאפשר להעביר לחדשים.' },
              { n:'04', h:'מפת דרכים', b:'ויזואלית.\nסדר עדיפויות.\nמה ראשון.' },
            ].map(d => (
              <div key={d.n} className="svc-deliv-item reveal">
                <div className="svc-deliv-icon">{d.n}</div>
                <h3>{d.h}</h3>
                <p className="pre">{d.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FitSection
        fitFor={['ארגון שגדל מהר.\nהשיטה לא גדלה איתו','מנכ״ל שמרגיש שהכל עובר דרכו','צוות חדש שלא נקלט טוב','ישיבות שלא יוצאות עם תוצרים','מוכנים לשמוע דברים לא נעימים']}
        notFor={['מי שמחפש אישור למה שכבר חושב','מי שלא מוכן לשנות איך עובדים','ארגון שצריך CHRO במשרה מלאה','מי שמחפש פתרון טכנולוגי']}
      />

      <FAQSection items={[
        { q:'כמה זמן זה לוקח?', a:'תהליך טיפוסי, מספר שבועות.\nהדוח הראשוני מוקדם יותר.\nמפת הדרכים בסוף.' },
        { q:'אצטרך להשבית את העסק?', a:'לא.\nהשיחות נערכות סביב סדר היום הקיים.\nהצפייה היא במציאות.\nהארגון ממשיך לעבוד כרגיל.' },
        { q:'מה אם הצוות לא ישתף פעולה?', a:'השאלה היא איך נכנסים.\nפוקוס מוד לא נכנסת בתור מבקר.\nאלא בתור מי שעוזר לראות את התמונה המלאה.\nרוב האנשים, גם המתנגדים, רוצים שהעבודה שלהם תרוץ טוב יותר.' },
        { q:'האם זה ייעוץ HR?', a:'לא.\nזה לא ייעוץ ארגוני קלאסי שעוסק במבנה הארגוני.\nוזה לא ייעוץ HR שעוסק באנשים ספציפיים.\nזה ייעוץ דרכי עבודה.\nאיך הדברים זזים בפועל.' },
        { q:'מי מבצע את ההמלצות בפועל?', a:'הארגון.\nפוקוס מוד נותנת מפה.\nההובלה הפנימית עושה את העבודה.\nליווי המשך אפשרי אם יש בו ערך.' },
        { q:'האם זה רלוונטי לארגון של 5 עובדים?', a:'כן.\nבמיוחד אם גדלים מהר.\nהשיטות שעבדו ב-5 לא יעבדו ב-15.\nעדיף למפות לפני שזה נהיה כאוס.' },
      ]} />

      <LeadFormBand
        defaultService="consulting"
        title={<>הארגון <span className="hl-block tilt-r">צמח מהר מהשיטה</span>?</>}
        body={'כמה אנשים, מה תקוע, מה השתנה לאחרונה.\nמתחילים בשיחת היכרות.'}
      />
    </main>
  );
}

// ─── ROUTER ───────────────────────────────────────────────────
export default function ServicePage() {
  const { slug } = useParams();
  if (slug === 'web' || slug === 'ai-web') return <WebServicePage />;
  if (slug === 'landing-pages') return <LandingPagesServicePage />;
  if (slug === 'systems') return <SystemsServicePage />;
  if (slug === 'automations') return <AutomationsServicePage />;
  // PMServicePage and ConsultingServicePage are written in full above and were
  // simply never reached from here. sitemap.xml and llms.txt have been pointing
  // Google and the AI crawlers at both slugs the whole time, so the two pages
  // were not merely hidden — they were answering "שירות לא נמצא".
  //
  // They are deliberately absent from SERVICES_INDEX and the header nav: Yoav
  // wants them findable, not led with, because they are the expensive
  // engagements. Where they surface in the site's own navigation is still open.
  if (slug === 'project-management') return <PMServicePage />;
  if (slug === 'consulting') return <ConsultingServicePage />;
  return (
    <main style={{ padding: '120px 0', textAlign: 'center' }}>
      <SEO title="שירות לא נמצא · FocusMode" />
      <div className="wrap">
        <h1>שירות לא נמצא</h1>
        <Link to="/services" className="btn btn-primary" style={{ marginTop: '24px', display: 'inline-flex' }}>לכל השירותים</Link>
      </div>
    </main>
  );
}
